/**
 * DsosIcon - Premium icon wrapper with standardized sizing, stroke, and colors
 * Enforces consistent iconography across marketing and app
 */

import * as React from 'react';
import { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

export type IconSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';
export type IconColor = 'default' | 'muted' | 'primary' | 'foreground' | 'muted-foreground';

interface DsosIconProps {
  icon: LucideIcon;
  size?: IconSize;
  color?: IconColor;
  strokeWidth?: number;
  className?: string;
}

const sizeMap: Record<IconSize, string> = {
  xs: 'w-3 h-3',      // 12px - inline micro
  sm: 'w-4 h-4',      // 16px - inline
  md: 'w-5 h-5',      // 20px - cards
  lg: 'w-6 h-6',      // 24px - section headers
  xl: 'w-8 h-8',      // 32px - hero/feature
};

const colorMap: Record<IconColor, string> = {
  default: 'text-foreground',
  muted: 'text-muted-foreground',
  primary: 'text-primary',
  foreground: 'text-foreground',
  'muted-foreground': 'text-muted-foreground',
};

export function DsosIcon({
  icon: Icon,
  size = 'md',
  color = 'muted',
  strokeWidth = 1.5,
  className,
}: DsosIconProps) {
  return (
    <Icon
      className={cn(sizeMap[size], colorMap[color], className)}
      strokeWidth={strokeWidth}
      aria-hidden="true"
    />
  );
}

